import redPin from '@/assets/images/red-pin.png';
import greenPin from '@/assets/images/green-pin.png';
import yellowPin from '@/assets/images/yellow-pin.png';

if (window.hasLoadMapScript === undefined) {
  window.hasLoadMapScript = false;
}

const key = '335858900df6512ae00fba47fe3bc8fb';

export default class MapService {
  constructor(containerId,
    {
      showControl,
      mapType,
    } = {
      showControl: true,
      mapType: 'satellite',
    }) {
    this.containerId = containerId;
    this.startCenter = [110, 32];
    this.startZoom = 6;
    this.zooms = [5, 18];
    this.mapInstance = null;
    this.infoWindow = null;
    this.markers = [];
    this.showControl = showControl;
    this.mapType = mapType;
    this.LngLat = null;
    this.Point = null;
    this.cluster = null;
    this.localSearch = null;
  }

  static isReady = false;

  static loadScript() {
    const scriptElement = document.querySelector('script[src*=tianditu]');

    if (window.hasLoadMapScript) return;

    if (!scriptElement) {
      const script = document.createElement('script');
      script.src = `http://api.tianditu.gov.cn/api?v=4.0&tk=${key}`;
      document.body.append(script);
      window.hasLoadMapScript = true;
      window.checkTMapReady();
    }
  }

  /**
   * 解析返回事件的经纬度
   */
  static parseLngLatFromEvent(e) {
    const lng = e.lnglat.getLng();
    const lat = e.lnglat.getLat();
    return { lng, lat };
  }

  /**
   * 更新地图缩放等级
   */
  setZoom(level) {
    if (this.mapInstance) {
      this.mapInstance.setZoom(level);
    }
  }

  /**
   * 更新地图中心
   */
  setCenter(position) {
    if (this.mapInstance) {
      this.mapInstance.panTo(this.wrapPosition(position));
    }
  }

  /**
   * 初始化地图对象
   */
  initMap(cb) {
    if (!MapService.isReady) {
      setTimeout(() => {
        this.initMap(cb);
      }, 1000);
      return;
    }

    const { Map, LngLat, Point } = window.T;

    const mapInstance = new Map(this.containerId, {
      center: new LngLat(...this.startCenter),
      zoom: this.startZoom,
      minZoom: this.zooms[0],
      maxZoom: this.zooms[1],
    });

    mapInstance.addControl(window.TMAP_HYBRID_MAP);
    this.mapInstance = mapInstance;
    this.LngLat = LngLat;
    this.Point = Point;
    this.initInfoWindow();
    this.initCluster();
    if (cb) cb();
  }

  /**
   * 初始化信息窗体单例
   */
  initInfoWindow() {
    const { InfoWindow, Point } = window.T;
    this.infoWindow = new InfoWindow('', {
      offset: new Point(0, -30),
    });
    this.infoWindow.setMaxWidth(450);
  }

  /**
   * 设置信息窗体内容
   */
  setInfoWindowContent(content) {
    this.infoWindow.setContent(content);
  }

  /**
   * 在特定标注物上打开信息窗体
   */
  openMarkerInfoWindow(markerId) {
    const marker = this.findMarkerOrError(markerId);
    this.infoWindow.setLngLat(marker.getLngLat());
    this.mapInstance.addOverLay(this.infoWindow);
  }

  /**
   * 在特定坐标打开信息窗体
   */
  openInfoWindow(position) {
    this.infoWindow.setLngLat(this.wrapPosition(position));
    this.mapInstance.addOverLay(this.infoWindow);
  }

  /**
   * 地图坐标适配方法
   */
  wrapPosition(position) {
    const { LngLat } = this;
    if (position instanceof LngLat) {
      return position;
    }
    const { lng, lat } = position;
    return new LngLat(lng, lat);
  }

  /**
   * 关闭信息窗体
   */
  closeInfoWindow() {
    this.infoWindow.closeInfoWindow();
  }

  /**
   * 添加标记物
   */
  addMarker({
    lat,
    lng,
    color = 'green',
    id = new Date().getTime(),
  }) {
    if (lat === undefined || lng === undefined) throw new Error('标记点经纬度参数错误');

    const {
      Point, Marker, LngLat, Icon,
    } = window.T;
    const iconConfig = {
      iconSize: new Point(30, 30),
      iconAnchor: new Point(15, 30),
    };
    const greenIcon = new Icon({ ...iconConfig, iconUrl: greenPin });
    const yellowIcon = new Icon({ ...iconConfig, iconUrl: yellowPin });
    const redIcon = new Icon({ ...iconConfig, iconUrl: redPin });

    let icon;
    switch (color) {
      case 'green':
      case 'success':
        icon = greenIcon;
        break;
      case 'yellow':
      case 'warning':
        icon = yellowIcon;
        break;
      case 'red':
      case 'danger':
        icon = redIcon;
        break;
      default:
        throw new Error('标记点颜色参数错误');
    }
    const position = new LngLat(lng, lat);
    const markerOptions = {
      icon,
      id,
      color,
    };
    let marker = this.findMarker(id);
    if (marker) {
      this.updateMarker(marker, position);
      return marker;
    }
    marker = new Marker(position, {
      ...markerOptions,
    });
    this.markers.push(marker);
    this.mapInstance.addOverLay(marker);
    return marker;
  }

  /**
   * 更新标记物坐标
   */
  updateMarker(marker, position) {
    marker.setLngLat(this.wrapPosition(position));
  }

  /**
   * 清空标记物
   */
  clearMarkers() {
    this.mapInstance.clearOverLays();
    this.markers = [];
  }

  /**
   * 通过ID获取标记物
   */
  findMarker(markerId) {
    return this.markers.find(m => m.options.id === markerId);
  }

  /**
   * 通过ID获取标记物，找不到会报错
   */
  findMarkerOrError(markerId) {
    const marker = this.findMarker(markerId);
    if (!marker) throw new Error('找不到标记点');
    return marker;
  }

  /**
   * 绑定标记物事件
   */
  addListenerToMarker(markerId, event, listener) {
    if (typeof listener !== 'function') {
      throw new Error('标记点事件监听器参数错误');
    }

    const marker = this.findMarkerOrError(markerId);

    switch (event) {
      case 'click':
        marker.on(event, listener);
        break;
      case 'mouseover':
        marker.on(event, listener);
        break;
      case 'mouseout':
        marker.on(event, listener);
        break;
      default:
        throw new Error('标记点监听事件错误');
    }
  }

  /**
   * 绑定地图事件
   */
  addListenerToMap(event, listener) {
    if (typeof listener !== 'function') {
      throw new Error('地图事件监听器参数错误');
    }

    const mapInstance = this.mapInstance;
    if (!mapInstance) {
      throw new Error('地图未初始化');
    }

    switch (event) {
      case 'click':
        mapInstance.on(event, listener);
        break;
      case 'rightclick':
        mapInstance.on('contextmenu', listener);
        break;
      default:
        throw new Error('标记点监听事件错误');
    }
  }

  /**
   * 渲染聚合点
   */
  renderCluster() {
    this.mapInstance.clearOverLays();
    this.cluster.clearMarkers();
    this.cluster.addMarkers(this.markers);
    this.cluster.setMaxZoom(13);
  }

  /**
   * 初始化点聚合
   */
  initCluster() {
    this.cluster = new window.T.MarkerClusterer(this.mapInstance,
      this.markers,
      {
        maxZoom: 13,
      });
  }

  placeSearch(keyword, cb) {
    const { LocalSearch } = window.T;
    const localSearch = new LocalSearch(this.mapInstance, {
      pageCapacity: 10,
      onSearchComplete(result) {
        if (cb) cb(result);
      },
    });
    localSearch.search(keyword, 4);
  }

  getLocationPosition(address, cb) {
    const { Geocoder } = window.T;
    const geocoder = new Geocoder();
    geocoder.getPoint(address, (result) => {
      if (result.getStatus() === 0) {
        cb(this.wrapPosition(result.getLocationPoint()));
      }
    });
  }
}

window.checkTMapReady = () => {
  setTimeout(() => {
    if (window.T) {
      MapService.isReady = true;
    } else {
      window.checkTMapReady();
    }
  }, 1000);
};

